import { memo, useMemo, useEffect } from "react";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { selectSnapshot, selectSnapshots } from "../../selectors/security";
import { toLocaleStringIn } from "../../util/general";
import { useStyles } from "./styles";
import { GroupedPosition } from "../../reducer/positions.reducer";
import { Typography } from "@mui/material";

export type ProfitCellProps = {
    position: GroupedPosition,
    onBasketProfitUpdate?: (data: { unrealized: number; realized: number; total: number }) => void,
}

const ProfitCell: React.FC<ProfitCellProps> = ({position, onBasketProfitUpdate}) => {

    const {classes, cx} = useStyles();
    const securityKey = getSecurityKey(position.security.exchange, position.security.code);
    const snapshots = useSelector(selectSnapshots);
    const snapshot = useSelector(selectSnapshot(securityKey))

    // For basket/strategy positions
    const basketProfitData = useMemo(() => {
        const { security, realizedProfit, children = [] } = position;
        if (position.children?.length <= 1) return { unrealized: 0, realized: 0, total: 0 };
        if (!snapshots) return { unrealized: 0, realized: 0, total: 0 };

        let unrealizedProfit = 0;
        children.forEach(childPos => {
            const childSecKey = getSecurityKey(childPos.security.exchange, childPos.security.code);
            const childSnapshot = snapshots[childSecKey];
            if (childSnapshot) {
                unrealizedProfit += (childSnapshot.lastTradedPrice - childPos.avgPrice)*childPos.quantity;
            }
        });

        const unrealizedConverted = unrealizedProfit / (security.priceMultiplier ?? 100);
        const realizedConverted = realizedProfit / (security.priceMultiplier ?? 100);

        return {
            unrealized: unrealizedConverted,
            realized: realizedConverted,
            total: unrealizedConverted + realizedConverted
        };
    }, [position, snapshots])

    // Call the callback when basket profit data changes
    useEffect(() => {
        if (onBasketProfitUpdate && position.children?.length > 1) {
            onBasketProfitUpdate(basketProfitData);
        }
    }, [basketProfitData, onBasketProfitUpdate, position.children?.length]);

    // For single positions
    const singleProfitData = useMemo(() => {
        const { security, avgPrice, realizedProfit, quantity } = position;
        if (position.children?.length > 1) return { unrealized: 0, realized: 0, total: 0 };
        if (!snapshot) return { unrealized: 0, realized: 0, total: 0 };

        const unrealizedConverted = ((snapshot.lastTradedPrice - avgPrice)*quantity) / (security.priceMultiplier ?? 100);
        const realizedConverted = realizedProfit / (security.priceMultiplier ?? 100);

        return {
            unrealized: unrealizedConverted,
            realized: realizedConverted,
            total: unrealizedConverted + realizedConverted
        };
    }, [position, snapshot]);

    const profitData = useMemo(() => position.children?.length > 1 ? basketProfitData : singleProfitData, [basketProfitData, singleProfitData, position]);

    const isBasketFullySquaredOff = useMemo(() => {
        if (position.children?.length <= 1) return false;
        return position.children.every(child => child.quantity === 0);
    }, [position.children]);
    
    const displayProfit = position.children?.length > 1 
    ? (isBasketFullySquaredOff ? profitData.realized : profitData.unrealized)
    : (position.quantity !== 0 ? profitData.unrealized : profitData.realized);
    const displayColor = displayProfit > 0 ? classes.profitColor : displayProfit < 0 ? classes.lossColor : undefined;

    return <>
        <Typography
            variant="body2"
            className={cx(displayColor)}
            style={{ textAlign: 'right' }}
        >
            {displayProfit}
        </Typography>
    </>;
}


export default memo(ProfitCell);